/**
 * Haptic Feedback Utility
 * Provides different vibration patterns for various user interactions
 */

export type HapticPattern = 'success' | 'error' | 'warning' | 'light' | 'medium' | 'heavy' | 'selection';

/**
 * Vibration patterns in milliseconds
 * Format: [vibrate, pause, vibrate, pause, ...]
 */
const HAPTIC_PATTERNS: Record<HapticPattern, number | number[]> = {
  // Success: Double tap (confirm action completed)
  success: [50, 50, 50],
  
  // Error: Three short bursts (alert user to problem)
  error: [100, 50, 100, 50, 100],
  
  // Warning: Single long vibration (caution)
  warning: [200],
  
  // Light: Very subtle tap (minor interaction)
  light: 10,
  
  // Medium: Standard tap (button press)
  medium: 30,
  
  // Heavy: Strong tap (important action)
  heavy: 50,
  
  // Selection: Quick double tap (item selected)
  selection: [25, 25, 25],
};

/**
 * Trigger haptic feedback with the specified pattern
 * @param pattern - The type of haptic feedback to trigger
 * @returns boolean - Whether vibration was triggered
 */
export const triggerHaptic = (pattern: HapticPattern): boolean => {
  // Check if the Vibration API is supported
  if (!('vibrate' in navigator)) {
    console.debug('Vibration API not supported on this device');
    return false;
  }

  try {
    const vibrationPattern = HAPTIC_PATTERNS[pattern];
    navigator.vibrate(vibrationPattern);
    return true;
  } catch (error) {
    console.error('Error triggering haptic feedback:', error);
    return false;
  }
};

/**
 * Legacy function for backward compatibility
 */
export const triggerHapticFeedback = (pattern: 'light' | 'medium' | 'heavy' = 'medium') => {
  triggerHaptic(pattern);
};

/**
 * Stop all vibrations
 */
export const stopHaptic = (): void => {
  if ('vibrate' in navigator) {
    navigator.vibrate(0);
  }
};

/**
 * Check if haptic feedback is supported
 */
export const isHapticSupported = (): boolean => {
  return 'vibrate' in navigator;
};
